import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Search as SearchIcon } from "lucide-react";
import { Seo, Reveal } from "../components/Seo";
import { PageHero } from "../components/blocks";
import { INSIGHTS } from "../lib/site";

export default function Insights() {
  const [q, setQ] = useState("");
  const filtered = INSIGHTS.filter((i) => `${i.title} ${i.excerpt} ${i.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Seo
        title="Insights — Softline Nigeria"
        description="Perspectives on digital products, identity, document services and enterprise technology from the Softline Nigeria team."
        path="/insights"
      />
      <PageHero
        eyebrow="Insights"
        title={["Notes from the", "engineering floor."]}
        lead="Short, practical perspectives on the problems we work on — document services, identity and community fintech."
        crumbs={[{ label: "Home", to: "/" }, { label: "Insights" }]}
      />

      <section className="bg-white" data-testid="insights-list">
        <div className="shell py-20 md:py-28">
          <Reveal>
            <div className="border border-lime bg-lime/10 p-5 flex items-start gap-3" data-testid="insights-draft-banner">
              <FileText size={18} className="text-charcoal mt-0.5 shrink-0" aria-hidden="true" />
              <p className="text-sm text-charcoal/80 leading-relaxed">
                <span className="font-semibold">Draft content.</span> The articles below are clearly marked placeholders
                prepared for editorial review — they will be reviewed, approved and dated before commercial publication.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-10 relative max-w-md" data-testid="insights-search">
              <label htmlFor="insights-q" className="sr-only">Filter insights</label>
              <SearchIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" aria-hidden="true" />
              <input
                id="insights-q"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter articles…"
                data-testid="insights-search-input"
                className="w-full border border-charcoal/25 bg-white pl-11 pr-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-lime focus:border-lime"
              />
            </div>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06} className="h-full">
                <article className="h-full flex flex-col border border-charcoal/10 hover:border-charcoal/30 transition-[border-color] duration-200" data-testid={`insight-card-${a.slug}`}>
                  <Link to={`/insights/${a.slug}`} className="flex flex-col flex-1 p-8" data-testid={`insight-link-${a.slug}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55">{a.category}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/40">{a.readTime}</span>
                    </div>
                    <h2 className="font-display font-bold text-xl mt-5 leading-snug">{a.title}</h2>
                    <p className="mt-4 text-sm leading-relaxed text-charcoal/65">{a.excerpt}</p>
                    <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-semibold">
                      Read draft <ArrowUpRight size={15} className="text-lime" />
                    </span>
                  </Link>
                </article>
              </Reveal>
            ))}
            {!filtered.length ? (
              <div className="md:col-span-2 lg:col-span-3 border border-charcoal/15 p-10 text-center" data-testid="insights-empty-state">
                <p className="font-display font-bold text-xl">No articles match “{q}”.</p>
                <p className="text-sm text-charcoal/60 mt-2">Try a different keyword, or clear the filter.</p>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </>
  );
}
